import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import styled from 'styled-components';
import { useLanguage } from '../../context/LanguageContext';
import LazyImage from '../ui/LazyImage';

const CardLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  height: 100%;
`;

const Card = styled.article`
  background-color: ${props => props.theme.colors.card};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
  transition: all 0.3s;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.elevated};
    transform: translateY(-0.25rem);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 7rem;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    height: 8rem;
  }
  
  @media (min-width: 768px) {
    height: 9rem;
  }
  
  @media (min-width: 1024px) {
    height: 11rem;
  }
  
  @media (min-width: 1440px) {
    height: 13rem;
  }
  
  @media (min-width: 1920px) {
    height: 14rem;
  }
`;

const ColorIndicator = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background-color: ${props => {
    const color = props.color;
    return props.theme.colors.category[color] || props.theme.colors.primary;
  }};
  
  @media (min-width: 1024px) {
    top: 1.5rem;
    left: 1.5rem;
    width: 1rem;
    height: 1rem;
  }
  
  @media (min-width: 1440px) {
    top: 2rem;
    left: 2rem;
    width: 1.25rem;
    height: 1.25rem;
  }
  
  @media (min-width: 1920px) {
    top: 2.5rem;
    left: 2.5rem;
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const Content = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  
  @media (min-width: 640px) {
    padding: 1.125rem;
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    padding: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    padding: 1.5rem;
  }
  
  @media (min-width: 1920px) {
    padding: 1.75rem;
  }
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
  
  h3 {
    font-family: ${props => props.theme.typography.fonts.display};
    font-size: 1.125rem;
    font-weight: ${props => props.theme.typography.fontWeights.semibold};
    margin-bottom: 0.25rem;
    transition: color 0.3s;
    
    @media (min-width: 640px) {
      font-size: 1.25rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1.375rem;
      margin-bottom: 0.375rem;
    }
    
    @media (min-width: 1024px) {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }
    
    @media (min-width: 1440px) {
      font-size: 1.875rem;
      margin-bottom: 0.625rem;
    }
    
    @media (min-width: 1920px) {
      font-size: 2.25rem;
      margin-bottom: 0.75rem;
    }
    
    ${Card}:hover & {
      color: ${props => props.theme.colors.primary};
    }
  }
  
  p {
    font-size: 0.875rem;
    color: ${props => props.theme.colors.mutedForeground};
    margin-top: 0;
    
    @media (min-width: 640px) {
      font-size: 0.9375rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1rem;
    }
    
    @media (min-width: 1024px) {
      font-size: 1.125rem;
    }
    
    @media (min-width: 1440px) {
      font-size: 1.25rem;
    }
    
    @media (min-width: 1920px) {
      font-size: 1.375rem;
    }
  }
`;

const IconButton = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: ${props => props.theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  flex-shrink: 0;
  margin-left: 1rem;
  
  @media (min-width: 640px) {
    width: 2.75rem;
    height: 2.75rem;
  }
  
  @media (min-width: 768px) {
    width: 3rem;
    height: 3rem;
    margin-left: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    width: 3.5rem;
    height: 3.5rem;
    margin-left: 1.5rem;
  }
  
  @media (min-width: 1440px) {
    width: 4.5rem;
    height: 4.5rem;
    margin-left: 2rem;
  }
  
  @media (min-width: 1920px) {
    width: 5.5rem;
    height: 5.5rem;
    margin-left: 2.5rem;
  }
  
  ${Card}:hover & {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primaryForeground};
    
    svg {
      transform: translateX(2px);
    }
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    transition: transform 0.3s;
    
    @media (min-width: 640px) {
      width: 1.375rem;
      height: 1.375rem;
    }
    
    @media (min-width: 768px) {
      width: 1.5rem;
      height: 1.5rem;
    }
    
    @media (min-width: 1024px) {
      width: 1.75rem;
      height: 1.75rem;
    }
    
    @media (min-width: 1440px) {
      width: 2.25rem;
      height: 2.25rem;
    }
    
    @media (min-width: 1920px) {
      width: 2.75rem;
      height: 2.75rem;
    }
  }
`;

const CategoryCard = ({ name, slug, color, imageUrl, postCount }) => {
  const { language, getCategoryUrl } = useLanguage();
  const categorySlug = slug || name?.toLowerCase();
  const categoryUrl = getCategoryUrl(categorySlug, language);
  
  return (
    <CardLink to={categoryUrl}>
      <Card>
        <ImageContainer>
          <LazyImage src={imageUrl} alt={`${name} category`} />
          <ColorIndicator color={color} />
        </ImageContainer>

        <Content>
          <TextContent>
            <h3>{name}</h3>
            <p>{postCount} articles</p>
          </TextContent>
          <IconButton>
            <ArrowRight />
          </IconButton>
        </Content>
      </Card>
    </CardLink>
  );
};

export default CategoryCard;
